import React, { useState, useRef, useEffect } from 'react';
import { ALargeSmall } from 'lucide-react';
import {
  TEXT_SIZES,
  DENSITIES,
  FONTS,
  TEXT_SIZE_LABELS,
  DENSITY_LABELS,
  FONT_LABELS,
  getTypography,
  setTypography,
  resetTypography,
} from '../config/typography';
import { track } from '../utils/analytics';
import { schedulePush, PREFERENCES_APPLIED_EVENT } from '../config/preferencesSync';
import { THEMES, THEME_LABELS, getTheme, setTheme } from '../config/theme';

/*
 * Typography preferences panel (#199): base text size, layout density and
 * font choice (including the self-hosted dyslexia-friendly OpenDyslexic).
 * Lives in the header beside the theme toggle so it is reachable signed-out
 * on every page — these are local reading preferences, like theme, not
 * account features. The values apply instantly via config/typography.js
 * (data attributes on <html>, defined in index.css) and persist in
 * localStorage; theme-bootstrap.js re-applies them before first paint.
 *
 * Dropdown chrome mirrors SystemSelector (outside-click + Escape close),
 * minus hover-open: a settings panel the pointer merely crosses should not
 * spring open.
 */
export default function TypographyMenu() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(getTypography);
  const [theme, setThemeState] = useState(getTheme);
  const wrapperRef = useRef(null);

  // A synced preferences apply (issue 291) rewrites the typography bag and
  // the theme key underneath React — resync the panel's pressed states.
  useEffect(() => {
    const onApplied = () => {
      setPrefs(getTypography());
      setThemeState(getTheme());
    };
    window.addEventListener(PREFERENCES_APPLIED_EVENT, onApplied);
    return () => window.removeEventListener(PREFERENCES_APPLIED_EVENT, onApplied);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const update = (partial) => {
    const next = setTypography(partial);
    setPrefs(next);
    track('typography', next);
    // Synced preferences (issue 291): push the change to the account
    // (no-op signed out).
    schedulePush();
  };

  // Theme set (#314): its own storage key and applier (config/theme.js), but
  // the same panel — one place a reader shapes how the site reads. The
  // applied event tells Layout's sun/moon toggle to resync its icon, the same
  // signal a synced server apply sends.
  const chooseTheme = (value) => {
    const next = setTheme(value);
    setThemeState(next);
    track('theme-set', { theme: next });
    schedulePush();
    try {
      window.dispatchEvent(new Event(PREFERENCES_APPLIED_EVENT));
    } catch (e) { /* non-browser environment */ }
  };

  const group = (label, values, labels, key, current, onSelect) => (
    <div className="mb-3 last:mb-0">
      <div
        className="text-xs font-semibold uppercase tracking-wider mb-1.5"
        style={{ color: 'var(--chrome-text)' }}
      >
        {label}
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label={label}>
        {values.map((value) => {
          const active = (current !== undefined ? current : prefs[key]) === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={active}
              className="dropdown-item"
              style={{
                padding: '0.25rem 0.625rem',
                borderRadius: '0.3rem',
                border: `1px solid ${active ? 'var(--gold)' : 'var(--chrome-border)'}`,
                color: active ? 'var(--chrome-text-active)' : 'var(--chrome-text)',
                background: active ? 'var(--chrome-hover)' : 'transparent',
              }}
              onClick={() => (onSelect ? onSelect(value) : update({ [key]: value }))}
            >
              {labels[value]}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="dropdown" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="chrome-icon-button"
        aria-expanded={open}
        aria-controls="typography-panel"
        aria-label="Typography"
        title="Text size, font & theme"
      >
        <ALargeSmall style={{ width: '1.1rem', height: '1.1rem' }} />
      </button>

      {open && (
        <div
          id="typography-panel"
          className="dropdown-menu"
          role="group"
          aria-label="Typography preferences"
          style={{ left: 'auto', right: 0, minWidth: '14rem', padding: '0.75rem 1rem' }}
        >
          {group('Text size', TEXT_SIZES, TEXT_SIZE_LABELS, 'size')}
          {group('Density', DENSITIES, DENSITY_LABELS, 'density')}
          {group('Font', FONTS, FONT_LABELS, 'font')}
          {group('Theme', THEMES, THEME_LABELS, null, theme, chooseTheme)}
          <button
            type="button"
            className="dropdown-item w-full mt-2"
            style={{
              justifyContent: 'center',
              border: '1px solid var(--chrome-border)',
              borderRadius: '0.3rem',
              color: 'var(--chrome-text)',
              padding: '0.25rem 0.625rem',
            }}
            onClick={() => {
              const next = resetTypography();
              setPrefs(next);
              track('typography', { reset: true });
              schedulePush();
            }}
          >
            Reset to defaults
          </button>
        </div>
      )}
    </div>
  );
}
